/**
 * A classroom's cover, as an editor — the picture, and the way to change it.
 *
 * The picture is edited the profile's way, on purpose. `UserBanner` is the
 * component a person changes their own banner with, so the 3:1 band, the camera
 * affordance and the menu behind it (*camera*, *files*, *remove*) are the ones a
 * school has already met in the profile dialog — a second cover editor invented for
 * classrooms would be a second set of words for one idea
 * (see `#/screens/Profile/Header/EditProfileDialog`).
 *
 * The picture is held as bytes (`#/lib/media/coverPicture`) only while the form
 * is open. Saving uploads them, writes the room's public face so the blob stays
 * referenced, and sends the blob's cid to the server, which then serves the
 * cover as `coverUrl` (`./publishClassroomFace`).
 *
 * Three details are deliberate:
 *
 *  - **The pick shows before it is stored.** Somebody who chose a photo should be
 *    looking at it; the stored picture arrives a moment later and takes its place.
 *    If it cannot be read the picture they had comes back, with the reason under
 *    it — never an `Image` pointed at nothing.
 *  - **A second pick supersedes the first.** Reading a photo takes a moment, and a
 *    reader who changes their mind in the middle of one has to end up with the photo
 *    they chose *last*, not with whichever attempt happened to finish last. The
 *    counter below is the whole mechanism.
 *  - **A removal is immediate.** Taking the picture away touches no bytes at all, so
 *    it lands at once — and the same counter is what stops an attempt already in
 *    flight from putting the picture back afterwards.
 */
import {useCallback, useRef, useState} from 'react'
import {View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {coverPicture} from '#/lib/media/coverPicture'
import {type PickerImage} from '#/lib/media/picker.shared'
import {cleanError} from '#/lib/strings/errors'
import {logger} from '#/logger'
import {UserBanner} from '#/view/com/util/UserBanner'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'

export function ClassroomCover({
  cover,
  onChange,
  testID = 'classroomCover',
}: {
  /** The cover the classroom carries now, as kept bytes, or `''` for none. */
  cover: string
  /** The next cover: the picture's bytes, or `''` when the picture was taken away. */
  onChange: (cover: string) => void
  testID?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  /** The photo just picked, shown while it is being read into the store. */
  const [picked, setPicked] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  /** Which attempt is allowed to land. A newer pick bumps it. */
  const run = useRef(0)

  const onSelectNewBanner = useCallback(
    (img: PickerImage | null) => {
      const chosen = ++run.current
      setError('')

      /*
       * Taken away. The room goes back to its gradient now, and the counter bump
       * above is what stops an attempt already in flight from putting the picture
       * back afterwards.
       */
      if (!img) {
        setPicked(null)
        setBusy(false)
        onChange('')
        return
      }

      setPicked(img.path)
      setBusy(true)
      void coverPicture(img)
        .then(picture => {
          if (run.current !== chosen) return
          onChange(picture)
        })
        .catch(e => {
          logger.error('Failed to read a classroom cover', {error: e})
          if (run.current !== chosen) return
          setError(cleanError(e))
        })
        .finally(() => {
          if (run.current !== chosen) return
          setBusy(false)
          setPicked(null)
        })
    },
    [onChange],
  )

  const shown = picked ?? cover

  return (
    <View testID={testID} style={[a.gap_sm]}>
      <UserBanner
        banner={shown || undefined}
        onSelectNewBanner={onSelectNewBanner}
      />
      {busy ? (
        <Text
          testID={`${testID}-preparing`}
          style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {l`Preparing the cover…`}
        </Text>
      ) : null}
      {error ? (
        <Text
          testID={`${testID}-error`}
          style={[a.text_sm, {color: t.palette.negative_500}]}>
          {error}
        </Text>
      ) : null}
    </View>
  )
}
